import { Section } from '@advanza/advanza_generic'
import { Col, Row } from '@advanza/grid'
import { HybridTooltip, Icon, LinearLoader, PreIcon } from '@advanza/ui'
import clsx from 'clsx'
import { CSSProperties, MouseEvent, PropsWithChildren, ReactNode, useState } from 'react'
import style from './Card.module.css'

interface Props {
    className?: string
    classNameIcon?: string
    expandableStyle?: boolean
    header?: ReactNode
    icon?: string | ReactNode
    iconStyle?: CSSProperties
    id?: string
    infoHover?: ReactNode
    isLoading?: boolean
    s3?: boolean
    topRight?: ReactNode
    useToggle?: boolean
}

const TOGGLE_BUTTON_ID = '_toggle_button'

const Card = ({
    children,
    className,
    classNameIcon,
    id,
    s3 = false,
    isLoading,
    header,
    topRight,
    icon,
    iconStyle = {},
    useToggle,
    expandableStyle,
    infoHover = undefined,
}: PropsWithChildren<Props>) => {
    const [open, setOpen] = useState(!useToggle)

    const onSectionClick = (e: MouseEvent<HTMLElement>) => {
        // The toggle button owns its own click, otherwise it could never collapse the card again.
        const isToggleButton = (e.target as HTMLElement).parentElement?.id === TOGGLE_BUTTON_ID

        if (useToggle && !isToggleButton) {
            setOpen(true)
        }
    }

    return (
        <Section
            bg="white"
            className={clsx(
                style.root,
                expandableStyle && style.expandableStyle,
                open && 'open',
                className,
            )}
            id={id}
            onClick={onSectionClick}
            x
            y="plus"
            style={{ position: 'relative', cursor: open ? 'default' : 'pointer' }}>
            {isLoading && <LinearLoader absolute />}
            {header && (
                <Row between="xs" middle="xs">
                    <Col x>
                        {icon && typeof icon === 'string' ? (
                            <PreIcon
                                fontSize={21}
                                primColor
                                icon={icon}
                                style={iconStyle}
                                classNameIcon={classNameIcon}
                                s3={s3}>
                                <h5 className="card-h">{header}</h5>
                                {infoHover && (
                                    <HybridTooltip childrenContent={infoHover}>
                                        <span className="inline-flex ml-1">
                                            <Icon fontSize={18} name="info" />
                                        </span>
                                    </HybridTooltip>
                                )}
                            </PreIcon>
                        ) : (
                            <div className="card-h">
                                <h5 className={style.h}>
                                    {icon}
                                    {header}
                                </h5>
                            </div>
                        )}
                    </Col>
                    {topRight && <Col x>{topRight}</Col>}
                    {useToggle && (
                        <Col x>
                            <button id={TOGGLE_BUTTON_ID} onClick={() => setOpen(!open)}>
                                <Icon name={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
                            </button>
                        </Col>
                    )}
                </Row>
            )}

            {open && children}
        </Section>
    )
}

export default Card
